import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMaintenanceMode } from '@/hooks/useMaintenance';
import { useAuthStore } from '@/stores/auth-store';
import MaintenancePage from './MaintenancePage';
import MaintenanceBanner from './MaintenanceBanner';
import { BYPASS_PATHS, MAINTENANCE_FIRST_ANSWER_MS } from '@/config/maintenance';
import { canBypassMaintenance } from '@/lib/roles';

export default function MaintenanceGuard({ children }: { children: ReactNode }) {
  const { data: maintenance } = useMaintenanceMode();
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const location = useLocation();

  // Gate only on auth init (local; resolves fast — initialize() sets
  // isInitialized even when the profile fetch fails). Without it, Zustand's
  // persist middleware rehydrates a stale admin user from localStorage,
  // briefly making canBypass true and flashing site content.
  //
  // We deliberately do NOT block on the maintenance query for long. When the
  // API is unreachable that query hangs/retries for minutes, and blocking on
  // it renders the whole app blank-white for that entire window (this is the
  // fleet-wide-outage symptom from 2026-05-28). Failing open is safe: an
  // absent/errored maintenance payload yields `enabled === false` below, so we
  // render the site.
  //
  // But rendering the site at once, with no answer at all, flashes the
  // storefront before the maintenance page on a site that is in maintenance.
  // So: a returning visitor is shown what this browser last heard (the hook's
  // placeholder) straight away, and a first-time visitor waits for the answer
  // for at most MAINTENANCE_FIRST_ANSWER_MS, then gets the site.
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaited(true), MAINTENANCE_FIRST_ANSWER_MS);
    return () => clearTimeout(timer);
  }, []);
  if (!isInitialized) return null;
  if (maintenance === undefined && !waited) return null;

  // Compute all flags without early returns so the render tree is always
  // the same shape (2 slots in Fragment). This prevents React from
  // unmounting/remounting children when auth state changes mid-callback.
  const enabled = !!maintenance?.enabled;
  // Designers count here too: pre-launch tenants sit behind "Coming Soon" for
  // weeks and are the ones most likely to need design work, and the Design
  // Sandbox preview iframes the real storefront. Must match
  // MAINTENANCE_BYPASS_ROLES on the backend.
  const canSeeThroughMaintenance = canBypassMaintenance(user?.role);
  // Don't use maintenance?.bypass here — the server derives it from an HttpOnly
  // cookie that persists after logout, which would let logged-out users bypass.
  const canBypass = enabled && canSeeThroughMaintenance;
  const isBypassPath = BYPASS_PATHS.some((p) => location.pathname.startsWith(p));

  const showBanner = enabled && canBypass && !isBypassPath;
  const showMaintenance = enabled && !canBypass && !isBypassPath;

  return (
    <>
      {showBanner && <MaintenanceBanner />}
      {showMaintenance ? (
        <MaintenancePage
          title={maintenance?.title}
          message={maintenance?.message}
          estimatedEnd={maintenance?.estimated_end}
        />
      ) : (
        children
      )}
    </>
  );
}
